// Single source of truth for the data-protection assent screen. The version
// string is logged with every consent record so the exact text a user agreed
// to can always be reconstructed for audit. Bump it whenever the copy below
// (or the linked policy documents) materially changes.
// Matches the active legal documents published 13 July 2026 (Privacy/GDPR v2,
// Terms/Code of Conduct v3). Bumping this re-prompts users whose stored consent
// predates it (see consentStorage.ts).
export const DATA_CONSENT_POLICY_VERSION = '2026-07-13.v2';

// TODO: replace with the registered legal data-controller entity name once
// confirmed by the organisation.
export const DATA_CONTROLLER_NAME = 'iElect';

// Backend legal-document type ids (GET {COREBASEURL}Legal/document-types).
export const LEGAL_DOCUMENT_TYPE = {
	termsOfService: 1,
	privacyPolicy: 2,
	gdprConsent: 3,
	volunteerCodeOfConduct: 4,
} as const;

export interface PolicyDocumentLink {
	// Visible label; rendered as "Read the full {label}".
	label: string;
	// The document title shown in the reader dialog.
	title: string;
	documentType: number;
}

// The links shown under the consent summary. Each opens the matching published
// document (fetched via getLegalDocumentsAction) in the reader dialog.
// NDPR notice maps to the GDPR consent document — NDPR is Nigeria's
// data-protection regulation and that document covers lawful-basis consent.
export const POLICY_DOCUMENTS: PolicyDocumentLink[] = [
	{ label: 'Privacy Policy', title: 'Privacy Policy', documentType: LEGAL_DOCUMENT_TYPE.privacyPolicy },
	{ label: 'Terms & Conditions', title: 'Terms & Conditions', documentType: LEGAL_DOCUMENT_TYPE.termsOfService },
	{ label: 'NDPR notice', title: 'NDPR Notice', documentType: LEGAL_DOCUMENT_TYPE.gdprConsent },
	{
		label: 'Volunteer Code of Conduct',
		title: 'Volunteer Code of Conduct',
		documentType: LEGAL_DOCUMENT_TYPE.volunteerCodeOfConduct,
	},
];

export interface ConsentSummarySection {
	id: string;
	heading: string;
	body: string;
}

export const CONSENT_SUMMARY: ConsentSummarySection[] = [
	{
		id: 'what',
		heading: 'What we collect',
		body: "Your name, contact details and date of birth; a live selfie (facial data); your voter's card image and VIN; your location on election day; and your bank details for payout.",
	},
	{
		id: 'why',
		heading: 'Why we collect it',
		body: 'To confirm you are a real, registered voter, verify your identity, assign you to a polling unit, and pay you for your work as a volunteer.',
	},
	{
		id: 'who',
		heading: 'Who controls your data',
		body: `${DATA_CONTROLLER_NAME} is the data controller and processes your data solely for this volunteer programme. We do not sell your data.`,
	},
	{
		id: 'rights',
		heading: 'Your rights & retention',
		body: "Under the GDPR and Nigeria's NDPR you can access, correct or delete your data, and withdraw consent at any time. We keep your data only as long as needed for the election and any legal obligations.",
	},
];

export interface ConsentOption {
	id: 'dataProcessing' | 'biometricVerification' | 'futureContact';
	required: boolean;
	title: string;
	description: string;
}

export const CONSENT_OPTIONS: ConsentOption[] = [
	{
		id: 'dataProcessing',
		required: true,
		title: 'Privacy Policy, Terms & data processing',
		description: `I agree to the Privacy Policy and Terms, and consent to ${DATA_CONTROLLER_NAME} processing my personal data for this volunteer application.`,
	},
	{
		id: 'biometricVerification',
		required: true,
		title: 'Identity & biometric verification',
		description: "I explicitly consent to my selfie/facial data and voter's card being collected and used to verify my identity.",
	},
	{
		id: 'futureContact',
		required: false,
		title: 'Future opportunities',
		description:
			"I'm happy to be contacted about future volunteering opportunities. (Optional — leaving this unchecked won't affect your application.)",
	},
];
